import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { TopNav, RoleBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Megaphone, Timer, Zap, EyeOff, CheckCircle2, AlertTriangle, FileText, Download, Volume2, VolumeX } from "lucide-react";
import AdminPanel from "@/components/AdminPanel";
import WinnerReveal from "@/components/WinnerReveal";
import GavelHit from "@/components/GavelHit";
import { useAuctionSounds } from "@/hooks/useAuctionSounds";
import { useAuctionState, useCountdown } from "@/hooks/useAuctionState";
import AllRosters from "@/components/auction/AllRosters";
import BidLog from "@/components/auction/BidLog";
import CallPlayerDialog from "@/components/auction/CallPlayerDialog";
import ParticipantsHud from "@/components/auction/ParticipantsHud";

export default function AuctionLive() {
  const { id } = useParams();
  const { user } = useAuth();
  const [bidAmt, setBidAmt] = useState("");
  const [secretAmt, setSecretAmt] = useState("");
  const [muted, setMuted] = useState(false);
  const sounds = useAuctionSounds();

  const { room, livePlayer, reveal, clearReveal } = useAuctionState(id, {
    onAssigned: () => { if (!muted) sounds.fireworks(); },
  });

  const remaining = useCountdown(room?.auction?.timer_ends_at);

  // Countdown beeps on last 5 seconds
  useEffect(() => {
    if (!room || room.auction?.status !== "live" || remaining == null) return;
    if (!muted) sounds.playCountdown(remaining);
  }, [remaining, room, muted, sounds]);
  useEffect(() => { sounds.resetCountdown(); }, [room?.auction?.player_id, sounds]);

  // Memoized derived lists (hooks must run before any early return)
  const activeTeams = useMemo(() => (room?.teams || []).filter((t) => t.user_id), [room?.teams]);

  if (!room) {
    return <div><TopNav /><div className="p-10 text-center text-slate-400">Caricamento asta…</div></div>;
  }

  const auction = room.auction;
  const isSecret = room.auction_mode === "secret";
  const myTeam = room.teams.find((t) => t.user_id === user?.id);
  const callerId = room.turn_order?.[room.turn_index % (room.turn_order?.length || 1)];
  const callerTeam = room.teams.find((t) => t.id === callerId);
  const isMyTurnToCall = myTeam && callerTeam && myTeam.id === callerTeam.id && auction?.status === "idle";
  const currentBid = auction?.current_bid || 0;
  const highBidderTeam = room.teams.find((t) => t.id === auction?.high_bidder_team_id);

  const sealedBids = auction?.sealed_bids || {};
  const mySealed = myTeam ? sealedBids[myTeam.id] : undefined;
  const submittedIds = auction?.sealed_submitted_team_ids || Object.keys(sealedBids);
  const tiebreakIds = auction?.tiebreak_teams || [];
  const isInTiebreak = tiebreakIds.length > 0;
  const canBidSecret = myTeam && (!isInTiebreak || tiebreakIds.includes(myTeam.id));

  const totalSlots = Object.values(room.slots).reduce((a, b) => a + Number(b), 0);
  const rosterSize = myTeam?.players?.length || 0;
  const remainingSlotsAfter = totalSlots - rosterSize - 1;
  const budgetWarning = (amt) => {
    if (!myTeam) return null;
    const n = Number(amt);
    if (!n || n <= 0) return null;
    const left = myTeam.credits - n;
    if (left < remainingSlotsAfter) return `Sfori il budget! Ti resterebbero ${left} FM per ${remainingSlotsAfter} slot.`;
    if (left < remainingSlotsAfter * 2 && remainingSlotsAfter > 0) {
      return `Attenzione: ti resterebbero solo ${left} FM per ${remainingSlotsAfter} slot (~${Math.floor(left / remainingSlotsAfter)} FM/slot).`;
    }
    return null;
  };

  const placeBid = async (amt) => {
    try {
      await api.post(`/rooms/${id}/bid`, { amount: Number(amt) });
      toast.success(`Offerta ${amt} FM piazzata!`);
      setBidAmt("");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warning("Qualcuno ha rilanciato un istante prima di te — riprova!");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
  };
  const placeSecretBid = async () => {
    const n = Number(secretAmt);
    if (n < 0) return;
    try {
      await api.post(`/rooms/${id}/secret-bid`, { amount: n });
      toast.success(`Offerta segreta di ${n} FM registrata`);
      setSecretAmt("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const timerCritical = remaining !== null && remaining <= 3;

  return (
    <div>
      <TopNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[10px] uppercase tracking-widest text-red-400 font-black">Live • {room.code}</span>
              <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded ${isSecret ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                {isSecret ? "Segreta" : "Classica"}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">{room.name}</h1>
          </div>
          <div className="glass rounded-xl px-4 py-2 flex items-center gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Chiama ora</div>
              <div className="font-bold text-emerald-400" data-testid="current-caller">{callerTeam?.name || "—"}</div>
            </div>
          </div>
          <Button data-testid="mute-toggle" size="sm" variant="outline"
                  onClick={() => { setMuted(!muted); if (muted) sounds.ensureCtx(); }}
                  className="border-white/10 hover:border-emerald-500/40"
                  title={muted ? "Attiva suoni" : "Disattiva suoni"}>
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </Button>
          {room.creator_id === user?.id && <AdminPanel room={room} onChange={() => { /* polling re-fetches */ }} />}
          {room.phase === "finished" && (
            <div className="flex gap-2">
              <Button data-testid="dl-pdf" size="sm" onClick={() => window.open(`${API_BASE}/rooms/${room.id}/export.pdf`, "_blank")}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
                <FileText className="w-4 h-4 mr-1" />PDF
              </Button>
              <Button data-testid="dl-csv" size="sm" onClick={() => window.open(`${API_BASE}/rooms/${room.id}/export.csv`, "_blank")}
                      variant="outline" className="border-emerald-500/30">
                <Download className="w-4 h-4 mr-1" />CSV
              </Button>
            </div>
          )}
        </div>

        {room.phase === "finished" && (
          <div className="glass rounded-2xl p-5 mb-4 border-emerald-500/30 bg-emerald-500/5" data-testid="finished-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <div className="flex-1">
                <div className="font-black text-lg">Lega chiusa — rose complete</div>
                <div className="text-sm text-slate-400">Scarica il PDF con tutte le rose o il CSV per Fantacalcio.it.</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          <div className="lg:col-span-8 space-y-4">
            {auction?.status === "live" && livePlayer ? (
              <div className={`relative pitch-bg grain rounded-3xl p-6 sm:p-8 border ${timerCritical ? "timer-critical" : "border-white/10 emerald-glow"}`} data-testid="auction-hero">
                {isInTiebreak && (
                  <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="tiebreak-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <b className="text-amber-400">Spareggio in corso!</b> Solo <b>{tiebreakIds.map((tid) => room.teams.find((t) => t.id === tid)?.name).filter(Boolean).join(" e ")}</b> possono rilanciare (nuova offerta segreta).
                    </div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                  <div className="flex items-center gap-4">
                    <RoleBadge role={livePlayer.role} className="w-14 h-14 text-2xl" />
                    <div>
                      <div className="label-eyebrow mb-1">In asta ora</div>
                      <h2 className="text-3xl sm:text-4xl font-black">{livePlayer.name}</h2>
                      <div className="text-sm text-slate-400">{livePlayer.team} • Quotazione base {livePlayer.quotation} FM</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="label-eyebrow mb-1" style={{ color: timerCritical ? "#F87171" : "#34D399" }}>
                      <Timer className="w-3 h-3 inline mr-1" /> Timer
                    </div>
                    <div className={`font-mono text-5xl font-black ${timerCritical ? "text-red-400" : "text-emerald-400"}`} data-testid="timer">
                      {remaining ?? "—"}s
                    </div>
                    <div className="mt-2 flex justify-end">
                      <GavelHit tick={remaining} active={typeof remaining === "number" && remaining > 0 && remaining <= 5} size={54} />
                    </div>
                  </div>
                </div>

                {isSecret ? (
                  <SecretPanel
                    submittedIds={submittedIds}
                    activeTeams={activeTeams}
                    tiebreakIds={tiebreakIds}
                    isInTiebreak={isInTiebreak}
                    totalSubmittable={(isInTiebreak ? tiebreakIds : room.turn_order).length}
                    mySealed={mySealed}
                    myTeam={myTeam}
                    canBidSecret={canBidSecret}
                    secretAmt={secretAmt}
                    setSecretAmt={setSecretAmt}
                    placeSecretBid={placeSecretBid}
                    budgetWarning={budgetWarning}
                    teams={room.teams}
                  />
                ) : (
                  <ClassicPanel
                    currentBid={currentBid}
                    highBidderTeam={highBidderTeam}
                    myTeam={myTeam}
                    bidAmt={bidAmt}
                    setBidAmt={setBidAmt}
                    placeBid={placeBid}
                    budgetWarning={budgetWarning}
                    timerSeconds={room.timer_seconds}
                  />
                )}
              </div>
            ) : room.phase === "finished" ? (
              <FinishedCard roomId={room.id} />
            ) : (
              <IdleCard callerTeamName={callerTeam?.name}>
                {isMyTurnToCall && (
                  <CallPlayerDialog roomId={id} soldPlayerIds={room.sold_player_ids || []} isSecret={isSecret} />
                )}
              </IdleCard>
            )}

            <ParticipantsHud
              teams={activeTeams}
              callerId={callerTeam?.id}
              myId={myTeam?.id}
              highBidderId={auction?.high_bidder_team_id}
              isSecret={isSecret}
              auctionLive={auction?.status === "live"}
            />

            <AllRosters activeTeams={activeTeams} myTeam={myTeam} totalSlots={totalSlots} />
          </div>

          <div className="lg:col-span-4">
            <BidLog logs={room.logs} />
          </div>
        </div>
      </div>
      <WinnerReveal event={reveal} onDone={clearReveal} />
    </div>
  );
}

// --- Local sub-components (kept here because they share closures with AuctionLive) ---

function ClassicPanel({ currentBid, highBidderTeam, myTeam, bidAmt, setBidAmt, placeBid, budgetWarning, timerSeconds }) {
  const warn = budgetWarning(bidAmt || currentBid + 1);
  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-1">Offerta corrente</div>
          <div className="font-mono text-5xl font-black text-white" data-testid="current-bid">
            {currentBid} <span className="text-lg text-slate-400">FM</span>
          </div>
          <div className="text-sm text-slate-400 mt-1">
            {highBidderTeam ? <>di <span className="text-emerald-400 font-bold">{highBidderTeam.name}</span></> : <span className="italic">Nessuna offerta ancora — parti tu!</span>}
          </div>
        </div>
      </div>

      {myTeam && (
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="label-eyebrow mb-3">Rilancia — clicca per inviare subito</div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="quick-bid-row">
            {[1, 5, 10].map((inc) => (
              <Button key={inc} data-testid={`quick-bid-${inc}`} onClick={() => placeBid(currentBid + inc)}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black h-12 px-6 text-lg">
                +{inc}
              </Button>
            ))}
          </div>
          <div className="flex gap-2" data-testid="custom-bid-row">
            <Input data-testid="custom-bid-input" type="number" min={currentBid + 1}
                   placeholder={`Puntata personalizzata (> ${currentBid})`}
                   value={bidAmt} onChange={(e) => setBidAmt(e.target.value)}
                   className="bg-slate-900/60 border-white/10 h-12 font-mono flex-1" />
            <Button data-testid="custom-bid-btn"
                    onClick={() => { if (bidAmt) { placeBid(bidAmt); } }}
                    disabled={!bidAmt}
                    className="bg-slate-800 hover:bg-slate-700 text-white font-black h-12 px-6 border border-emerald-500/40">
              <Zap className="w-4 h-4 mr-2" />Offri
            </Button>
          </div>
          {warn && (
            <div className="text-xs text-amber-400 flex items-center gap-1 mt-2">
              <AlertTriangle className="w-3 h-3" />{warn}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-2">Ogni rilancio azzera il timer a {timerSeconds}s.</div>
        </div>
      )}
    </>
  );
}

function SecretPanel({ submittedIds, activeTeams, tiebreakIds, isInTiebreak, totalSubmittable, mySealed, myTeam, canBidSecret, secretAmt, setSecretAmt, placeSecretBid, budgetWarning, teams }) {
  const warn = budgetWarning(secretAmt);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl bg-slate-900/60 border border-white/5">
          <div className="label-eyebrow mb-1">Offerte ricevute</div>
          <div className="text-3xl font-black font-mono">
            {submittedIds.length}<span className="text-sm text-slate-500">/{totalSubmittable}</span>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-slate-900/60 border border-white/5 col-span-2 sm:col-span-2">
          <div className="label-eyebrow mb-1">La tua offerta</div>
          <div className="text-3xl font-black font-mono text-emerald-400" data-testid="my-secret-bid">
            {mySealed !== undefined ? `${mySealed} FM` : "—"}
          </div>
        </div>
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <div className="label-eyebrow mb-1" style={{ color: "#FBBF24" }}>
            <EyeOff className="w-3 h-3 inline mr-1" />Rivelate
          </div>
          <div className="text-sm text-slate-300 mt-1">Alla scadenza del timer</div>
        </div>
      </div>

      {myTeam && canBidSecret && (
        <div className="pt-4 border-t border-white/10">
          <div className="label-eyebrow mb-3">La tua offerta segreta</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input data-testid="secret-bid-input" type="number" min="0" placeholder="es. 42"
                   value={secretAmt} onChange={(e) => setSecretAmt(e.target.value)}
                   className="bg-slate-900/60 border-white/10 h-12 font-mono text-lg" />
            <Button data-testid="secret-bid-btn" onClick={placeSecretBid}
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black h-12 px-8">
              <EyeOff className="w-4 h-4 mr-2" />{mySealed !== undefined ? "Aggiorna offerta" : "Piazza offerta"}
            </Button>
          </div>
          {warn && (
            <div className="mt-2 text-xs text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />{warn}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-2">Puoi aggiornare la tua offerta fino allo scadere del timer. Vince l'importo più alto. In caso di parità, spareggio automatico.</div>
        </div>
      )}
      {myTeam && !canBidSecret && (
        <div className="text-sm text-slate-400 italic pt-4 border-t border-white/10">
          Non partecipi a questo spareggio. Osserva chi si aggiudicherà il calciatore.
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10" data-testid="submitted-badges">
        {activeTeams.map((t) => {
          const has = submittedIds.includes(t.id);
          const eligible = !isInTiebreak || tiebreakIds.includes(t.id);
          if (!eligible) return null;
          const _teamName = teams.find((x) => x.id === t.id)?.name || t.name;
          return (
            <div key={t.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold ${has ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-slate-800 text-slate-400 border border-white/5"}`}>
              {has && <CheckCircle2 className="w-3 h-3" />} {_teamName}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function IdleCard({ callerTeamName, children }) {
  return (
    <div className="pitch-bg grain rounded-3xl p-8 border border-white/10 text-center" data-testid="auction-idle">
      <Megaphone className="w-12 h-12 mx-auto text-emerald-400 mb-4" />
      <div className="label-eyebrow mb-2">In attesa di chiamata</div>
      <h2 className="text-3xl font-black mb-2">Tocca a <span className="text-emerald-400">{callerTeamName}</span></h2>
      <p className="text-slate-400 mb-6">Deve scegliere il prossimo calciatore da mettere all'asta.</p>
      {children}
    </div>
  );
}

function FinishedCard({ roomId }) {
  return (
    <div className="pitch-bg grain rounded-3xl p-10 border border-emerald-500/30 text-center" data-testid="auction-finished">
      <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400 mb-4" />
      <div className="label-eyebrow mb-2">Asta terminata</div>
      <h2 className="text-3xl font-black mb-2">Rose complete!</h2>
      <p className="text-slate-400 mb-6">Scarica il documento con tutte le squadre per condividerlo con la lega.</p>
      <div className="flex justify-center gap-2 flex-wrap">
        <Button data-testid="finished-dl-pdf" onClick={() => window.open(`${API_BASE}/rooms/${roomId}/export.pdf`, "_blank")}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-12 px-6">
          <FileText className="w-5 h-5 mr-2" />Scarica riepilogo PDF
        </Button>
        <Button data-testid="finished-dl-csv" onClick={() => window.open(`${API_BASE}/rooms/${roomId}/export.csv`, "_blank")}
                variant="outline" className="border-emerald-500/30 hover:bg-emerald-500/10 h-12 px-6">
          <Download className="w-5 h-5 mr-2" />Scarica CSV
        </Button>
      </div>
    </div>
  );
}

// Local helpers kept inline above; sub-sections (participants, rosters, log, dialog) live in /components/auction/
